import React, { useState, useEffect } from 'react';
import { PhoneNumberUtil } from "google-libphonenumber";
import { Form, FormGroup, Input, Label, Button, Container, Badge, Alert, Spinner } from 'reactstrap';
import HeadingWithSubheading from '../../../components/PageTitle/HeadingWithSubheading';
import SectionDivider from '../../../components/SectionDivider/SectionDivider';
import StateDropdown from '../../../components/StatesDropdown/StateDropdown';
import { CreateHQDataStorageKey, DefaultRegionStorageKey } from '../../../constants/localStorage';
import { ZipcodeRegex } from '../../../constants/regex';
import { useAPIContext } from '../../../utilities/Contexts/DCGAutomationPagesContextProvider';
import PostalZipCodeControl from '../../../components/Addresses/PostalZipCodeControl';

const CreateHQLocationPageContent = () => {
    const {navigateToPage} = useAPIContext();
    const phoneUtil = PhoneNumberUtil.getInstance();
    const [isLoading, setIsLoading] = useState(false);
    const country = parseInt(localStorage.getItem(DefaultRegionStorageKey)) ?? 1; // Default to 1 if no region is stored in localStorage, this is for inflight onboarding after release.
    // Define a single state object for all input fields
    const [formData, setFormData] = useState(() => {
        const formData = JSON.parse(localStorage.getItem(CreateHQDataStorageKey)) ?? {};
        formData.country = country;

        return formData
    });

    const [errors, setErrors] = useState({
        phone: '',
        zipcode: '',
        state: ''
    });

    useEffect(() => {
        if (formData) {
            localStorage.setItem(CreateHQDataStorageKey, JSON.stringify(formData));
        }

    }, [formData]);
    

    // Handle input changes and update the state object
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    function isValidCanadianPostalCode(postalCode, newErrors) {
        // Remove spaces and convert to uppercase for consistency
        const formattedPostalCode = postalCode.replace(/\s+/g, '').toUpperCase();

        // Regular expression for Canadian postal code validation
        const regex = /^[A-Z]\d[A-Z]\d[A-Z]\d$/;

        if(!regex.test(formattedPostalCode)){
            newErrors.zipcode = 'Invalid postal code format.'; 
        };
    }

    function isValidUsZipCode(zipcode, newErrors){
        const zipRegex = new RegExp(ZipcodeRegex);
        if (!zipRegex.test(zipcode)) {
            newErrors.zipcode = 'Invalid postal code format.';
        }
    }

    const validateForm = () => {
        setIsLoading(true);
        var newErrors = {};

        //validate Zip
        if (country === 1){
            isValidUsZipCode(formData.zipcode, newErrors);
        }
        else if (country === 2){
            isValidCanadianPostalCode(formData.zipcode, newErrors);
        } 
        else {    
            newErrors.zipcode = "Unknown country selected";   // Should never happen, but just in case 
        }

        //validate phone
        let isValidPhone = false;
        try {
            const number = phoneUtil.parse("+1-" + formData.phone);
            isValidPhone = phoneUtil.isValidNumber(number);
        } catch { }

        if (!isValidPhone) {
            newErrors.phone = '** Please enter a valid phone number in XXX-XXX-XXXX format';
        }
        //validate state
        var stateValue = formData.state ?? '';
        if (stateValue.toLowerCase() === '') {
            newErrors.state = (country === 1) ? 'Please select a state' : 'Please select a province';
        }

        setErrors(newErrors);
        setIsLoading(false);
        return Object.keys(newErrors).length === 0;
    }

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        // Process form data here (e.g., send to API)
        var isValid = validateForm();
        if (isValid) {
            navigateToPage('setStaffPermissions');
        }
    };

    const hqBadge = (
        <Badge
            style={{
                backgroundColor: '#1CABA8',
                fontSize: '14px',
                paddingTop: '5px',
                paddingLeft: '10px',
                paddingRight: '10px',
                borderRadius: '15px',
                color: 'white'
            }}
        >
            HQ
        </Badge>
    );

    const submitBtnTxt = isLoading ? <Spinner size="sm" /> : "NEXT"

    return (
        <Container className="text-center">
            <Form onSubmit={handleSubmit}>
                <FormGroup className="mb-3">
                    <Label for="company" hidden>
                        Organization Name
                    </Label>
                    <Input
                        id="company"
                        name="company"
                        placeholder="Organization Name"
                        type="text"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                    />
                </FormGroup>

                <SectionDivider width="70%" />

                <HeadingWithSubheading
                    heading="Enter organization's address"
                    subheading="*This will show as the Headquarters Location in the portal."
                    optionalLeft={hqBadge}
                />

                <FormGroup className="mb-3">
                    <Label for="address" hidden>
                        Street Address
                    </Label>
                    <Input
                        id="address"
                        name="address"
                        placeholder="Street Address"
                        type="text"
                        value={formData.address}
                        onChange={handleInputChange}
                        autoComplete="shipping street-address"
                        required
                    />
                </FormGroup>

                <FormGroup className="mb-3">
                    <Label for="address2" hidden>
                        Street Address2
                    </Label>
                    <Input
                        id="address2"
                        name="address2"
                        placeholder="Street Address2"
                        type="text"
                        value={formData.address2}
                        onChange={handleInputChange}
                    />
                </FormGroup>

                <FormGroup className="mb-3">
                    <Label for="city" hidden>
                        City
                    </Label>
                    <Input
                        id="city"
                        name="city"
                        placeholder="City"
                        type="text"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                    />
                </FormGroup>

                <StateDropdown
                    className="mb-3"
                    value={formData.state}
                    onChange={(state) => handleInputChange(state)}
                    country={country}
                />
                {errors.state && <Alert color="danger">{errors.state}</Alert>}
                <FormGroup className="mb-3">
                    <Label for="zipcode" hidden>
                        Postal Code
                    </Label>
                    <PostalZipCodeControl
                        id="zipcode"
                        name="zipcode"
                        placeholder={"Postal Code"}
                        defaultValue={formData.zipcode}
                        onChange={handleInputChange}
                        required
                        region={country}
                    />
                    {errors.zipcode && <Alert color="danger">{errors.zipcode}</Alert>}
                </FormGroup>

                <FormGroup className="mb-3">
                    <Label for="phone" hidden>
                        Company Phone
                    </Label>
                    <Input
                        id="phone"
                        name="phone"
                        placeholder="Company Phone"
                        type="text"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                    />
                    {errors.phone && <Alert color="danger">{errors.phone}</Alert>}
                </FormGroup>

                <Button className="btn-same-size" disabled={isLoading} type="submit" color="primary">
                    {submitBtnTxt}
                </Button>
            </Form>
        </Container>
    );
};

export default CreateHQLocationPageContent;
